import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Typography, Button, Tabs, Tab, Box,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress,
  IconButton, Collapse, Divider
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from './../../utils/DateUtils';
import { getClientInvoices } from './../../services/ClientService';
import { fetchInvoiceDetails } from './../../services/SalesService';
import { getPaymentLabel, getInvoiceStatus } from './../../utils/Enums';
import { formatMoney } from '../../utils/currency';

const TabPanel = ({ children, value, index, ...rest }) => (
  <div role="tabpanel" hidden={value !== index} id={`client-tabpanel-${index}`} aria-labelledby={`client-tab-${index}`} {...rest}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const ViewClientModal = ({ open, onClose, client, loyaltyCardEnabled = false }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [invoicesTotal, setInvoicesTotal] = useState(0);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesPage, setInvoicesPage] = useState(1);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);
  const [invoiceDetailsCache, setInvoiceDetailsCache] = useState({});
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const pageSize = 20;

  const fetchInvoices = useCallback(async () => {
    if (!client?.ci) return;
    setInvoicesLoading(true);
    try {
      const data = await getClientInvoices(client.ci, invoicesPage, pageSize);
      setInvoices(data?.invoices ?? []);
      setInvoicesTotal(data?.total ?? 0);
    } catch (err) {
      console.error('Error fetching client invoices:', err);
      setInvoices([]);
      setInvoicesTotal(0);
    } finally {
      setInvoicesLoading(false);
    }
  }, [client?.ci, invoicesPage]);

  useEffect(() => {
    if (open && client?.ci && tab === 1) {
      fetchInvoices();
    }
  }, [open, client?.ci, tab, fetchInvoices]);

  const handleClose = () => {
    setTab(0);
    setInvoices([]);
    setInvoicesTotal(0);
    setInvoicesPage(1);
    setExpandedInvoiceId(null);
    setInvoiceDetailsCache({});
    onClose();
  };

  if (!client) return null;

  const fields = [
    { label: t('ci'), value: client.ci },
    { label: t('first_name'), value: client.firstName },
    { label: t('last_name'), value: client.lastName },
    { label: t('email'), value: client.email },
    { label: t('phone'), value: client.phone },
    { label: t('total_purchases'), value: client.totalPurchases },
    { label: t('total_spent'), value: `${formatMoney(client.totalSpent ?? 0)}` },
    { label: t('average_ticket'), value: `${formatMoney(client.averageTicket ?? 0)}` },
    ...(loyaltyCardEnabled ? [{ label: t('loyalty_stamps') || 'Sellos acumulados', value: client.loyaltyStamps ?? 0 }] : []),
    { label: t('last_purchase'), value: client.lastPurchase ? formatDateTime(client.lastPurchase) : '-' }
  ];

  const formatStatus = (status) => getInvoiceStatus(status) ?? status;

  const handleExpandRow = async (invoiceId) => {
    if (expandedInvoiceId === invoiceId) {
      setExpandedInvoiceId(null);
      return;
    }
    setExpandedInvoiceId(invoiceId);
    if (invoiceDetailsCache[invoiceId]) return;
    setDetailLoadingId(invoiceId);
    try {
      const detail = await fetchInvoiceDetails(invoiceId);
      setInvoiceDetailsCache((prev) => ({ ...prev, [invoiceId]: detail }));
    } catch (e) {
      console.error('Error fetching invoice detail', e);
    } finally {
      setDetailLoadingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('view_client')}</DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="client tabs" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={t('info') || 'Información'} id="client-tab-0" aria-controls="client-tabpanel-0" />
          <Tab label={t('history') || 'Historial'} id="client-tab-1" aria-controls="client-tabpanel-1" />
        </Tabs>
        <Divider sx={{ mt: 0, mb: 0 }} />

        <TabPanel value={tab} index={0}>
          <Grid container spacing={2}>
            {fields.map(({ label, value }) => (
              <React.Fragment key={label}>
                <Grid item xs={6}>
                  <Typography variant="subtitle1"><strong>{label}</strong></Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>{value}</Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          {invoicesLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('transactions') || 'Transacciones'} ({invoicesTotal})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 48 }} />
                      <TableCell><strong>{t('date') || 'Fecha'}</strong></TableCell>
                      <TableCell align="right"><strong>{t('total') || 'Total'}</strong></TableCell>
                      <TableCell><strong>{t('location') || 'Sucursal'}</strong></TableCell>
                      <TableCell><strong>{t('status') || 'Estado'}</strong></TableCell>
                      <TableCell><strong>{t('payment_method') || 'Método de pago'}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">{t('no_transactions') || 'Sin transacciones'}</TableCell>
                      </TableRow>
                    ) : (
                      invoices.map((inv) => {
                        const isExpanded = expandedInvoiceId === inv.id;
                        const detail = invoiceDetailsCache[inv.id];
                        const loadingDetail = detailLoadingId === inv.id;
                        return (
                          <React.Fragment key={inv.id}>
                            <TableRow sx={{ '& > *': { borderBottom: isExpanded ? 'unset' : undefined } }}>
                              <TableCell sx={{ width: 48 }}>
                                <IconButton size="small" onClick={() => handleExpandRow(inv.id)} aria-label={isExpanded ? 'colapsar' : 'expandir'}>
                                  {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell>{formatDateTime(inv.createdAt)}</TableCell>
                              <TableCell align="right">{formatMoney(inv.total ?? 0)}</TableCell>
                              <TableCell>{inv.locationName ?? '-'}</TableCell>
                              <TableCell>{formatStatus(inv.status)}</TableCell>
                              <TableCell>{getPaymentLabel(inv.paymentMethod)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={6} sx={{ py: 0, borderBottom: 0 }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ pl: 3, pr: 2, pb: 2 }}>
                                    {loadingDetail ? (
                                      <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
                                    ) : detail?.items?.length ? (
                                      <>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t('items') || 'Desglose'}</Typography>
                                        <Table size="small" sx={{ minWidth: 320 }}>
                                        <TableHead>
                                          <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                            <TableCell>{t('product') || 'Producto'}</TableCell>
                                            <TableCell align="center">{t('quantity') || 'Cant.'}</TableCell>
                                            <TableCell align="right">{t('unit_price') || 'P. unit.'}</TableCell>
                                            <TableCell align="right">{t('subtotal') || 'Subtotal'}</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {detail.items.map((item, idx) => (
                                            <TableRow key={idx}>
                                              <TableCell>{item.productName ?? '-'}</TableCell>
                                              <TableCell align="center">{item.quantity ?? 0}</TableCell>
                                              <TableCell align="right">{formatMoney(item.unitPrice ?? 0)}</TableCell>
                                              <TableCell align="right">{formatMoney((item.quantity ?? 0) * ((item.unitPrice ?? 0) - (item.discountAmount ?? 0)))}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                      </>
                                    ) : detail && !detail.items?.length ? (
                                      <Typography variant="body2" color="text.secondary">{t('no_items') || 'Sin ítems'}</Typography>
                                    ) : null}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {invoicesTotal > pageSize && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Button size="small" disabled={invoicesPage <= 1} onClick={() => setInvoicesPage((p) => p - 1)}>
                    {t('previous') || 'Anterior'}
                  </Button>
                  <Typography variant="body2">
                    {t('page') || 'Página'} {invoicesPage} / {Math.ceil(invoicesTotal / pageSize)}
                  </Typography>
                  <Button size="small" disabled={invoicesPage >= Math.ceil(invoicesTotal / pageSize)} onClick={() => setInvoicesPage((p) => p + 1)}>
                    {t('next') || 'Siguiente'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewClientModal;
